import { FunctionComponent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import {
  ArrowRight,
  BookOpen,
  Building2,
  Check,
  CheckCircle2,
  Compass,
  Package,
  Quote,
  Star,
  Users,
} from 'lucide-react';
import { TopMenu } from '../../components/layout/TopMenu';
import { BallonMenu } from '../../basicComponents/accountMenu/BallonMenu';
import { PLANS_PATH, REGISTRATION_PATH } from '../../utils/constants';
import { LogoMarquee } from './LogoMarquee';
import { RevealLine } from './RevealLine';
import talkingFedup from '../../images/home/talking-fedup-2.webp';
import questionEmbarrassed from '../../images/home/question-embarrassed.webp';
import setupFrustration from '../../images/home/setup-frustration.webp';
import threeConnected from '../../images/home/three-connected.webp';
import teamBuildingFun from '../../images/home/team-building-fun.jpg';

// The cozyjuicyreal.com Self-Hosted page (cozyjuicyreal/src/pages/SelfHosted.tsx),
// told in the order of the Team BrandScript (cozyjuicyreal/branding), with sections
// adapted from the website's homepage (cozyjuicyreal/src/pages/Index.tsx). The team
// lead is the hero; here they run the sessions themselves, so the direct call to
// action is "Sign up free" and "Book a demo" stays the transitional one.

const WEBSITE_URL = 'https://cozyjuicyreal.com';
const TRUSTPILOT_URL = 'https://www.trustpilot.com/review/cozyjuicyreal.com';
const DEMO_VIDEO_URL = '/videos/online-question-levels-demo.webm';

const INCLUDED = [
  { icon: Package, t: 'Helps teams bond faster and collaborate better afterward' },
  { icon: BookOpen, t: 'Facilitation guide' },
  { icon: Compass, t: 'Context-specific prompt sets' },
  { icon: Users, t: 'Private facilitator community' },
];

const PROBLEMS = [
  {
    title: 'A few take the spotlight.',
    desc: "The same three people do all the talking. Your quietest teammates haven't unmuted in 40 minutes.",
    image: talkingFedup,
    alt: 'Exasperated orange fuzzy character with a hand on its forehead, rolling its eyes',
  },
  {
    title: 'Put on the spot.',
    desc: "Out of nowhere, someone's asked to share their deepest heartbreak. No warning, no warm-up, no option to pass.",
    image: questionEmbarrassed,
    alt: 'Embarrassed purple fuzzy character blushing and covering its mouth',
  },
  {
    title: 'Half the event is spent on setup.',
    desc: "Everyone's on the call and ready to go. Thirty minutes later, and you're still explaining the rules.",
    image: setupFrustration,
    alt: 'Frustrated yellow fuzzy character pulling at its hair',
  },
];

// Each answer mirrors a problem card above, in the same order and accent colour.
const GUIDE_ANSWERS = [
  {
    title: 'Everyone takes turns',
    desc: 'The game moves around a board, so your quietest teammate gets the same airtime as the loudest.',
    dot: 'var(--accent)',
  },
  {
    title: 'Depth by choice',
    desc: 'Questions build from light to meaningful, and every player chooses how deep to go.',
    dot: 'var(--brand-purple-soft)',
  },
  {
    title: 'Playing in minutes',
    desc: 'Open a game room, share the link, and your team is playing. No prep, no downloads, no tech headaches.',
    dot: 'var(--highlight)',
  },
];

// The three decks, in play order. Chip colours match the decks.
const DECKS = [
  {
    name: 'Cozy',
    bg: 'var(--accent)',
    fg: 'var(--accent-foreground)',
    desc: 'Easy warm-up questions that get everyone talking.',
  },
  {
    name: 'Juicy',
    bg: 'var(--highlight)',
    fg: 'var(--primary)',
    desc: 'Curious and playful, and yes, always work-appropriate.',
  },
  {
    name: 'Real',
    bg: 'var(--primary)',
    fg: 'var(--primary-foreground)',
    desc: 'Meaningful moments, always by choice.',
  },
];

const STEPS = [
  {
    n: '01',
    t: 'Choose your plan',
    d: 'You can sign up free and try the game with your team before choosing a plan.',
  },
  {
    n: '02',
    t: 'Run your first session',
    d: 'The platform is intuitive to use, and once you sign in, the How to page walks you through everything.',
  },
  {
    n: '03',
    t: 'Make it a ritual',
    d: 'Repeat quarterly, monthly, or per-team. Different game editions keep the experience fresh.',
  },
];

const VS = [
  { live: 'We facilitate every session', self: 'Run your own sessions' },
  { live: 'Per event pricing', self: 'Facilitated by someone on your team' },
  { live: 'Best for large, critical or complex events', self: 'Quick set up and user friendly' },
  { live: 'Pay what you choose, per session', self: 'Start free, upgrade anytime' },
  { live: 'Booking required for each session', self: 'Run it whenever, wherever, however often' },
  { live: 'Schedule with our team', self: 'Annual plans' },
];

// Results the team lead can picture weeks later; distinct from the guide's answers.
const SUCCESS_RESULTS = [
  {
    title: 'Teammates who actually know each other.',
    desc: 'The stories they share keep coming up in meetings and Slack for months.',
  },
  {
    title: 'Collaboration gets easier.',
    desc: "It's simpler to ask for help, share an idea, or disagree with someone you've laughed with.",
  },
  {
    title: 'A win you can point to.',
    desc: 'Your team thanks you instead of rolling their eyes, and the feedback speaks for itself.',
  },
];

const Stars: FunctionComponent<{ size: number }> = ({ size }) => (
  <StarRow aria-hidden="true">
    {[...Array(5)].map((_, i) => (
      <Star key={i} size={size} fill="currentColor" />
    ))}
  </StarRow>
);

export const HomePage: FunctionComponent = () => (
  <Page>
    <BallonMenu />
    <TopMenu />

    <main>
      {/* 1. The character: a team lead who wants connection as a ritual */}
      <Hero>
        <Container>
          <HeroGrid>
            <Stack>
              <Eyebrow>
                <Building2 size={14} /> Self-Hosted for Teams
              </Eyebrow>
              <HeroTitle>
                Make connection a <Highlight>team ritual</Highlight> — run by your own people.
              </HeroTitle>
              <HeroText>
                Equip your internal champions with the full Cozy Juicy Real toolkit. Everything
                they need to host confidently, spark real connection, and look suspiciously good
                at facilitation.
              </HeroText>
              <Actions>
                <PrimaryLink to={REGISTRATION_PATH}>
                  Sign up free <ArrowRight size={16} />
                </PrimaryLink>
                <OutlineLink to={PLANS_PATH}>See pricing</OutlineLink>
              </Actions>
              <Checks>
                <span>
                  <CheckCircle2 size={16} /> No facilitation experience needed
                </span>
                <span>
                  <CheckCircle2 size={16} /> Works for remote and hybrid teams
                </span>
              </Checks>
              <Proof>
                <Stars size={16} />
                <a href={TRUSTPILOT_URL} target="_blank" rel="noreferrer">
                  4.9/5 on Trustpilot
                </a>
                <span aria-hidden="true">·</span>
                <span>500+ teams</span>
                <span aria-hidden="true">·</span>
                <span>
                  <strong>97%</strong> feel more connected
                </span>
              </Proof>
            </Stack>

            <HeroCardWrap>
              <HeroGlow />
              <HeroCard>
                {INCLUDED.map(({ icon: Icon, t }) => (
                  <IncludedItem key={t}>
                    <IncludedIcon>
                      <Icon size={16} />
                    </IncludedIcon>
                    <IncludedText>{t}</IncludedText>
                  </IncludedItem>
                ))}
              </HeroCard>
            </HeroCardWrap>
          </HeroGrid>
        </Container>
      </Hero>

      <TrustStrip>
        <LogoMarquee />
      </TrustStrip>

      {/* 2. The problem: forced fun */}
      <Section $surface="lavender">
        <Container>
          <SectionHeading
            title={
              <>
                Please… <Highlight>not another</Highlight> trivia, escape room or happy hour.
              </>
            }
            subtitle="We get it. You want people to leave feeling closer, not counting down the minutes."
          />
          <SubHeading>Sound familiar?</SubHeading>
          <ProblemGrid>
            {PROBLEMS.map((p) => (
              <ProblemCard key={p.title}>
                <ProblemTitle>{p.title}</ProblemTitle>
                <ProblemText>{p.desc}</ProblemText>
                <ProblemImage>
                  <img
                    src={p.image}
                    alt={p.alt}
                    loading="lazy"
                    decoding="async"
                    width={600}
                    height={600}
                  />
                </ProblemImage>
              </ProblemCard>
            ))}
          </ProblemGrid>
        </Container>
      </Section>

      {/* Turning point: the philosophical line, revealed word by word */}
      <TurningPoint>
        <Container>
          <RevealLine text="Connecting a team meaningfully shouldn't be so hard." />
        </Container>
      </TurningPoint>

      {/* 3. The guide: empathy + authority */}
      <Section>
        <Container>
          <GuideGrid>
            <GuideFigure>
              <img
                src={teamBuildingFun}
                alt="Players laughing together around the Cozy Juicy Real board"
                loading="lazy"
                decoding="async"
              />
              <figcaption>
                <strong>97%</strong>
                <span>of players feel more connected to their team</span>
              </figcaption>
            </GuideFigure>
            <div>
              <SectionTitle $align="left">
                We've sat through the cringe <Nowrap>“get-to-know-you”</Nowrap> games too.
              </SectionTitle>
              <Lead>
                So we built an experience that does the opposite: tested with hundreds of teams
                around the world, it starts light, builds gradually, and gives everyone choice.
              </Lead>
              <BulletList>
                {GUIDE_ANSWERS.map((a) => (
                  <li key={a.title}>
                    <Dot $color={a.dot} aria-hidden="true" />
                    <p>
                      <strong>{a.title}:</strong> {a.desc}
                    </p>
                  </li>
                ))}
              </BulletList>
            </div>
          </GuideGrid>

          <ReviewCard>
            <RatingPanel href={TRUSTPILOT_URL} target="_blank" rel="noreferrer">
              <RatingScore>
                4.9<span>/5</span>
              </RatingScore>
              <Stars size={20} />
              <RatingLink>457 reviews on Trustpilot</RatingLink>
              <RatingNote>500+ teams worldwide</RatingNote>
            </RatingPanel>
            <Testimonial>
              <Quote size={32} color="var(--accent)" aria-hidden="true" />
              <blockquote>
                “Seemed a little daunting to get cozy with colleagues, but I was so impressed by
                the <Marker>thoughtful questions that created a space for meaningful conversations</Marker>
                .”
              </blockquote>
              <figcaption>
                <Avatar aria-hidden="true">JR</Avatar>
                <span>
                  <strong>Julia Romano</strong>
                  <span>Senior Customer Success Manager, HubSpot</span>
                </span>
              </figcaption>
            </Testimonial>
          </ReviewCard>
        </Container>
      </Section>

      {/* How it plays: the three decks, for anyone who wants to see what happens */}
      <Section $surface="lavender">
        <Container>
          <SectionHeading
            title="From work talk to real talk."
            subtitle="Open a room and share the link. The game guides everyone from light questions to meaningful ones, so your host doesn't have to."
          />
          <DecksGrid>
            <DemoVideo>
              <video
                src={DEMO_VIDEO_URL}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                aria-label="Demo of the online Cozy Juicy Real game moving through the Cozy, Juicy and Real question levels"
              />
            </DemoVideo>
            <DeckList>
              {DECKS.map((deck) => (
                <li key={deck.name}>
                  <DeckChip $bg={deck.bg} $fg={deck.fg}>
                    {deck.name}
                  </DeckChip>
                  <p>{deck.desc}</p>
                </li>
              ))}
            </DeckList>
          </DecksGrid>
        </Container>
      </Section>

      {/* 4. The plan */}
      <Section>
        <Container>
          <SectionHeading eyebrow="How it works" title="From sign-up to ritual in three steps" />
          <StepsGrid>
            {STEPS.map((s) => (
              <StepCard key={s.n}>
                <StepNumber>{s.n}</StepNumber>
                <StepTitle>{s.t}</StepTitle>
                <StepText>{s.d}</StepText>
              </StepCard>
            ))}
          </StepsGrid>
          <Centered>
            <PrimaryLink to={REGISTRATION_PATH}>
              Sign up free <ArrowRight size={16} />
            </PrimaryLink>
          </Centered>
        </Container>
      </Section>

      {/* Live vs Self-Hosted */}
      <Section $surface="cream">
        <Container>
          <SectionHeading
            eyebrow="Live vs self-hosted"
            title="Which one's right for you?"
            subtitle="The difference between events that we host, and events that you host."
          />
          <VsTable>
            <VsHeader>
              <div>Hosted by our team</div>
              <div>Hosted by you</div>
            </VsHeader>
            {VS.map((row) => (
              <VsRow key={row.live}>
                <div>{row.live}</div>
                <div>{row.self}</div>
              </VsRow>
            ))}
          </VsTable>
          <Centered>
            <TextLink href={`${WEBSITE_URL}/reserve-your-date`}>
              Prefer we host it? Reserve your date <ArrowRight size={16} aria-hidden="true" />
            </TextLink>
          </Centered>
        </Container>
      </Section>

      {/* 6–7. Success, with a pinch of failure in the opening line */}
      <Section>
        <Container>
          <SuccessGrid>
            <div>
              <SectionTitle $align="left">
                People stay where they <Highlight>feel seen</Highlight>.
              </SectionTitle>
              <Lead>
                Without psychological safety or a sense of belonging, good people quietly drift
                away. But belonging can be built, one real conversation at a time.
              </Lead>
              <BulletList $spacious>
                {SUCCESS_RESULTS.map((r) => (
                  <li key={r.title}>
                    <CheckBadge aria-hidden="true">
                      <Check size={14} strokeWidth={3} />
                    </CheckBadge>
                    <p>
                      <strong>{r.title}</strong> {r.desc}
                    </p>
                  </li>
                ))}
              </BulletList>
              <Actions $top={36}>
                <PrimaryLink to={REGISTRATION_PATH}>
                  Sign up free <ArrowRight size={16} />
                </PrimaryLink>
              </Actions>
            </div>
            {/* The same three characters from the problem section, now happy together. */}
            <SuccessFigure>
              <img
                src={threeConnected}
                alt="The orange, purple and yellow fuzzy characters sitting together, laughing and waving"
                loading="lazy"
                decoding="async"
                width={1200}
                height={800}
              />
            </SuccessFigure>
          </SuccessGrid>
        </Container>
      </Section>

      {/* Final call to action: the transformation, and the territory we own */}
      <Section>
        <Container>
          <CtaBox>
            <CtaGlowTop />
            <CtaGlowBottom />
            <CtaContent>
              <SectionTitle $light>
                This is where coworkers become people you{' '}
                <Nowrap>
                  <Highlight $onDark>actually know</Highlight>.
                </Nowrap>
              </SectionTitle>
              <CtaText>
                Most team building fills an hour. Cozy Juicy Real opens a door. One question sparks
                a story, a story sparks a laugh, and suddenly the person you've shared a Slack
                channel with for two years feels like someone you'd call a friend. Sometimes that
                friendship outlasts the job.
              </CtaText>
              <CtaPromise>Be the leader who builds teams people don't want to leave.</CtaPromise>
              <Actions $center $top={32}>
                <PrimaryLink to={REGISTRATION_PATH}>
                  Sign up free <ArrowRight size={16} />
                </PrimaryLink>
                <QuietOnDarkButton href={`${WEBSITE_URL}/book-a-demo`}>
                  Book a demo
                </QuietOnDarkButton>
              </Actions>
              <Tagline>Meaningful connection at work. It's all we do.</Tagline>
            </CtaContent>
          </CtaBox>
        </Container>
      </Section>
    </main>
  </Page>
);

const SectionHeading: FunctionComponent<{
  eyebrow?: string;
  title: ReactNode;
  subtitle?: ReactNode;
}> = ({ eyebrow, title, subtitle }) => (
  <HeadingWrap>
    {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
    <SectionTitle>{title}</SectionTitle>
    {subtitle ? <SectionSubtitle>{subtitle}</SectionSubtitle> : null}
  </HeadingWrap>
);

// Breakpoints and spacing follow the website's Tailwind config.
const sm = '@media (min-width: 640px)';
const md = '@media (min-width: 768px)';
const lg = '@media (min-width: 1024px)';
const xl = '@media (min-width: 1280px)';

const Page = styled('div')`
  width: 100%;
  background: var(--background);

  /* TopMenu leaves room for page content; the hero starts right below it here. */
  & > header {
    margin-bottom: 0;
  }
`;

// The website's container: 1.25rem gutters, capped at 1320px (as measured on cozyjuicyreal.com).
const Container = styled('div')`
  width: 100%;
  max-width: 1320px;
  margin: 0 auto;
  padding: 0 1.25rem;
`;

const Stack = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Centered = styled('div')`
  display: flex;
  justify-content: center;
  margin-top: 40px;
`;

const Nowrap = styled('span')`
  white-space: nowrap;
`;

const Eyebrow = styled('div')`
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 9999px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: var(--secondary);
  color: var(--primary);
`;

const Highlight = styled('span')<{ $onDark?: boolean }>`
  position: relative;
  display: inline-block;
  z-index: 0;

  &::before {
    content: '';
    position: absolute;
    inset: auto 0 0.05em 0;
    height: 0.45em;
    background: ${({ $onDark }) => ($onDark ? 'var(--brand-orange)' : 'var(--highlight)')};
    z-index: -1;
    border-radius: 0.25em;
    transform: skewX(-6deg);
  }
`;

const StarRow = styled('span')`
  display: inline-flex;
  gap: 2px;
  color: var(--rating);
`;

// Buttons

const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 44px;
  padding: 0 32px;
  border-radius: 9999px;
  font-family: var(--font-body);
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  text-decoration: none;
  transition: background-color 150ms, color 150ms;

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px var(--background), 0 0 0 4px var(--ring);
  }
`;

const PrimaryLink = styled(Link)`
  ${buttonBase}
  background: var(--accent);
  color: var(--accent-foreground);
  box-shadow: var(--shadow-soft);

  &:hover {
    background: hsla(27, 100%, 50%, 0.9);
  }
`;

const OutlineLink = styled(Link)`
  ${buttonBase}
  border: 1px solid var(--input);
  background: var(--background);
  color: var(--foreground);

  &:hover {
    background: var(--accent);
    color: var(--accent-foreground);
  }
`;

const QuietOnDarkButton = styled('a')`
  ${buttonBase}
  color: hsla(0, 0%, 100%, 0.8);

  &:hover {
    background: hsla(0, 0%, 100%, 0.15);
    color: var(--primary-foreground);
  }
`;

const TextLink = styled('a')`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 600;
  color: var(--primary);
  text-decoration: none;
  text-underline-offset: 4px;

  &:hover {
    text-decoration: underline;
  }
`;

const Actions = styled('div')<{ $center?: boolean; $top?: number }>`
  display: flex;
  flex-wrap: wrap;
  justify-content: ${({ $center }) => ($center ? 'center' : 'flex-start')};
  gap: 12px;
  padding-top: ${({ $top }) => ($top === undefined ? 8 : 0)}px;
  margin-top: ${({ $top }) => $top ?? 0}px;
`;

// Hero

const Hero = styled('section')`
  position: relative;
  overflow: hidden;
  background: var(--gradient-hero);
  padding: 48px 0 80px;

  ${md} {
    padding: 80px 0 112px;
  }
`;

const HeroGrid = styled('div')`
  display: grid;
  gap: 48px;
  align-items: center;

  ${lg} {
    grid-template-columns: 1fr 1fr;
    gap: 64px;
  }
`;

const HeroTitle = styled('h1')`
  margin: 0;
  font-weight: 700;
  font-size: 2.25rem;
  line-height: 2.5rem;
  text-wrap: balance;

  ${md} {
    font-size: 3.75rem;
    line-height: 1;
  }
`;

const HeroText = styled('p')`
  margin: 0;
  max-width: 36rem;
  font-size: 1.125rem;
  line-height: 1.75rem;
  color: hsla(263, 50%, 12%, 0.75);
  text-wrap: pretty;

  ${md} {
    font-size: 1.25rem;
  }
`;

const Checks = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 24px;
  padding-top: 12px;
  font-size: 14px;
  line-height: 20px;
  color: hsla(263, 50%, 12%, 0.65);

  span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  svg {
    color: var(--accent);
  }
`;

const Proof = styled('div')`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: hsla(263, 50%, 12%, 0.75);

  a {
    color: inherit;
    text-decoration: none;
  }

  a:hover {
    color: var(--primary);
    text-decoration: underline;
  }

  strong {
    font-weight: 600;
    color: var(--primary);
  }
`;

const HeroCardWrap = styled('div')`
  position: relative;
`;

const HeroGlow = styled('div')`
  position: absolute;
  inset: 0;
  background: var(--gradient-warm);
  border-radius: 2.5rem;
  transform: rotate(3deg);
  opacity: 0.3;
  filter: blur(40px);
`;

const HeroCard = styled('div')`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 32px;
  border: 1px solid var(--border);
  border-radius: 2rem;
  background: var(--card);
  box-shadow: var(--shadow-card);
`;

const IncludedItem = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 12px;
  background: hsla(252, 100%, 96%, 0.5);
`;

const IncludedIcon = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: var(--card);
  color: var(--primary);
  box-shadow: var(--shadow-soft);
`;

const IncludedText = styled('div')`
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: var(--primary);
`;

const TrustStrip = styled('section')`
  padding: 16px 0 32px;
  border-top: 1px solid hsla(252, 30%, 90%, 0.6);
  border-bottom: 1px solid hsla(252, 30%, 90%, 0.6);
  background: var(--background);

  ${md} {
    padding: 20px 0 40px;
  }
`;

// Sections

const surfaces = {
  default: 'var(--background)',
  lavender: 'var(--brand-lavender)',
  cream: 'var(--brand-cream)',
};

const Section = styled('section')<{ $surface?: keyof typeof surfaces }>`
  padding: 80px 0;
  background: ${({ $surface = 'default' }) => surfaces[$surface]};

  ${md} {
    padding: 112px 0;
  }
`;

const HeadingWrap = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  max-width: 48rem;
  margin: 0 auto 48px;
  text-align: center;

  & > ${Eyebrow} {
    align-self: center;
  }
`;

// Section headings sit about 85% of the website's hero headline.
const SectionTitle = styled('h2')<{ $light?: boolean; $align?: 'left' }>`
  margin: 0;
  font-weight: 700;
  font-size: 1.75rem;
  line-height: 1.12;
  text-wrap: balance;
  text-align: ${({ $align }) => $align ?? 'inherit'};
  color: ${({ $light }) => ($light ? 'var(--primary-foreground)' : 'var(--primary)')};

  ${sm} {
    font-size: 2.15rem;
  }

  ${md} {
    font-size: 2.65rem;
    line-height: 1.08;
  }

  ${xl} {
    font-size: 2.9rem;
  }
`;

const SectionSubtitle = styled('p')`
  margin: 0;
  font-size: 1.125rem;
  line-height: 1.75rem;
  color: hsla(263, 50%, 12%, 0.75);
  text-wrap: pretty;

  ${md} {
    font-size: 1.25rem;
  }
`;

const Lead = styled(SectionSubtitle)`
  margin-top: 16px;
`;

const SubHeading = styled('h3')`
  margin: 0 0 24px;
  text-align: center;
  font-weight: 700;
  font-size: 1.25rem;

  ${md} {
    font-size: 1.5rem;
  }
`;

// Problem

const ProblemGrid = styled('div')`
  display: grid;
  gap: 20px;

  ${md} {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const ProblemCard = styled('div')`
  display: flex;
  flex-direction: column;
  padding: 28px;
  border: 1px solid var(--border);
  border-radius: 24px;
  background: var(--card);
  box-shadow: var(--shadow-soft);
`;

const ProblemTitle = styled('h4')`
  margin: 0 0 8px;
  font-weight: 700;
  font-size: 1.125rem;
`;

const ProblemText = styled('p')`
  margin: 0;
  font-size: 14px;
  line-height: 1.625;
  color: hsla(263, 50%, 12%, 0.7);
`;

const ProblemImage = styled('div')`
  margin-top: auto;
  padding-top: 24px;

  img {
    display: block;
    width: 160px;
    height: 160px;
    margin: 0 auto;
    object-fit: contain;

    ${md} {
      width: 176px;
      height: 176px;
    }
  }
`;

const TurningPoint = styled('section')`
  padding: 64px 0;
  background: var(--primary);

  ${md} {
    padding: 80px 0;
  }

  p {
    max-width: 56rem;
    margin: 0 auto;
    text-align: center;
    font-family: var(--font-display);
    font-weight: 700;
    font-size: 1.75rem;
    line-height: 1.25;
    letter-spacing: -0.025em;
    color: var(--primary-foreground);
    text-wrap: balance;

    ${md} {
      font-size: 2.5rem;
    }

    ${lg} {
      max-width: none;
      font-size: 2.25rem;
      white-space: nowrap;
    }

    ${xl} {
      font-size: 2.5rem;
    }
  }
`;

// Guide

const GuideGrid = styled('div')`
  display: grid;
  gap: 48px;
  align-items: center;

  ${lg} {
    grid-template-columns: 0.95fr 1.05fr;
    gap: 64px;
  }
`;

const GuideFigure = styled('figure')`
  position: relative;
  margin: 0;

  img {
    display: block;
    width: 100%;
    aspect-ratio: 4 / 3;
    object-fit: cover;
    border: 1px solid var(--border);
    border-radius: 24px;
    box-shadow: var(--shadow-card);
  }

  figcaption {
    position: absolute;
    bottom: 16px;
    left: 16px;
    display: flex;
    align-items: center;
    gap: 12px;
    max-width: 15rem;
    padding: 12px 16px;
    border: 1px solid var(--border);
    border-radius: 16px;
    background: hsla(0, 0%, 100%, 0.95);
    box-shadow: var(--shadow-soft);
    backdrop-filter: blur(8px);

    ${md} {
      bottom: 24px;
      left: 24px;
    }
  }

  figcaption strong {
    font-family: var(--font-display);
    font-weight: 800;
    font-size: 1.875rem;
    line-height: 1;
    color: var(--accent);
  }

  figcaption span {
    font-size: 14px;
    font-weight: 600;
    line-height: 1.375;
    color: var(--primary);
  }
`;

const BulletList = styled('ul')<{ $spacious?: boolean }>`
  display: grid;
  gap: ${({ $spacious }) => ($spacious ? 16 : 14)}px;
  margin: ${({ $spacious }) => ($spacious ? 32 : 28)}px 0 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    gap: 14px;
  }

  p {
    margin: 0;
    font-size: 1rem;
    line-height: 1.625;
    color: hsla(263, 50%, 12%, 0.75);
  }

  strong {
    font-family: var(--font-display);
    font-weight: 700;
    color: var(--primary);
  }
`;

const Dot = styled('span')<{ $color: string }>`
  flex-shrink: 0;
  width: 12px;
  height: 12px;
  margin-top: 0.45rem;
  border-radius: 9999px;
  background: ${({ $color }) => $color};
`;

const CheckBadge = styled('span')`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-top: 2px;
  border-radius: 9999px;
  background: var(--highlight);
  color: var(--primary);
`;

const ReviewCard = styled('div')`
  display: grid;
  overflow: hidden;
  margin-top: 64px;
  border: 1px solid var(--border);
  border-radius: 24px;
  background: var(--card);
  box-shadow: var(--shadow-soft);

  ${md} {
    grid-template-columns: minmax(0, 16rem) 1fr;
  }
`;

const RatingPanel = styled('a')`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px 24px;
  background: hsla(252, 100%, 96%, 0.7);
  text-align: center;
  text-decoration: none;
  transition: background-color 150ms;

  &:hover {
    background: var(--secondary);
  }
`;

const RatingScore = styled('span')`
  font-family: var(--font-display);
  font-weight: 800;
  font-size: 3rem;
  line-height: 1;
  letter-spacing: -0.025em;
  color: var(--primary);

  span {
    font-size: 1.5rem;
    font-weight: 700;
    color: hsla(263, 84%, 33%, 0.45);
  }
`;

const RatingLink = styled('span')`
  font-size: 14px;
  font-weight: 600;
  color: var(--primary);
  text-underline-offset: 4px;

  ${RatingPanel}:hover & {
    text-decoration: underline;
  }
`;

const RatingNote = styled('span')`
  font-size: 12px;
  font-weight: 500;
  color: hsla(263, 50%, 12%, 0.6);
`;

const Testimonial = styled('figure')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 20px;
  margin: 0;
  padding: 28px;

  ${md} {
    padding: 40px;
  }

  blockquote {
    margin: 0;
    font-family: var(--font-display);
    font-weight: 600;
    font-size: 1.25rem;
    line-height: 1.375;
    color: var(--primary);
    text-wrap: pretty;

    ${md} {
      font-size: 1.5rem;
    }
  }

  figcaption {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  figcaption strong {
    display: block;
    font-weight: 600;
    color: var(--primary);
  }

  figcaption strong + span {
    display: block;
    font-size: 14px;
    color: hsla(263, 50%, 12%, 0.65);
  }
`;

const Marker = styled('span')`
  background: linear-gradient(transparent 60%, hsla(51, 100%, 51%, 0.55) 60%);
  -webkit-box-decoration-break: clone;
  box-decoration-break: clone;
`;

const Avatar = styled('span')`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 9999px;
  background: var(--primary);
  font-family: var(--font-display);
  font-size: 14px;
  font-weight: 700;
  color: var(--primary-foreground);
`;

// How it plays

const DecksGrid = styled('div')`
  display: grid;
  gap: 40px;
  align-items: center;

  ${lg} {
    grid-template-columns: 1.1fr 0.9fr;
    gap: 56px;
  }
`;

const DemoVideo = styled('figure')`
  overflow: hidden;
  margin: 0;
  border: 1px solid var(--border);
  border-radius: 24px;
  background: var(--card);
  box-shadow: var(--shadow-card);

  video {
    display: block;
    width: 100%;
    aspect-ratio: 16 / 9;
    object-fit: cover;
  }
`;

const DeckList = styled('ul')`
  display: grid;
  gap: 20px;
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    align-items: flex-start;
    gap: 16px;
  }

  p {
    margin: 0;
    font-size: 1rem;
    line-height: 1.625;
    color: hsla(263, 50%, 12%, 0.75);
  }
`;

const DeckChip = styled('span')<{ $bg: string; $fg: string }>`
  display: inline-flex;
  flex-shrink: 0;
  justify-content: center;
  width: 80px;
  margin-top: 2px;
  padding: 4px 0;
  border-radius: 9999px;
  background: ${({ $bg }) => $bg};
  font-family: var(--font-display);
  font-size: 14px;
  font-weight: 700;
  color: ${({ $fg }) => $fg};
`;

// Plan

const StepsGrid = styled('div')`
  display: grid;
  gap: 20px;
  max-width: 72rem;
  margin: 0 auto;

  ${md} {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const StepCard = styled('div')`
  padding: 24px;
  border: 1px solid var(--border);
  border-radius: 16px;
  background: var(--card);
  box-shadow: var(--shadow-soft);
`;

const StepNumber = styled('div')`
  margin-bottom: 12px;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 1.875rem;
  line-height: 2.25rem;
  color: var(--accent);
`;

const StepTitle = styled('h3')`
  margin: 0 0 6px;
  font-weight: 600;
  font-size: 1.125rem;
  line-height: 1.75rem;
`;

const StepText = styled('p')`
  margin: 0;
  font-size: 14px;
  line-height: 1.625;
  color: hsla(263, 50%, 12%, 0.7);
`;

// Live vs Self-Hosted

const VsTable = styled('div')`
  max-width: 64rem;
  margin: 0 auto;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 24px;
  background: var(--card);
  box-shadow: var(--shadow-soft);
`;

const vsRow = css`
  display: grid;
  grid-template-columns: 1fr 1fr;

  & > div {
    padding: 20px;
  }

  & > div:first-child {
    border-right: 1px solid var(--border);
  }
`;

const VsHeader = styled('div')`
  ${vsRow}
  background: var(--secondary);
  color: var(--primary);
  font-family: var(--font-display);
  font-weight: 600;
`;

const VsRow = styled('div')`
  ${vsRow}
  border-top: 1px solid var(--border);
  color: hsla(263, 50%, 12%, 0.8);
`;

// Success

const SuccessGrid = styled('div')`
  display: grid;
  gap: 48px;
  align-items: center;

  ${lg} {
    grid-template-columns: 1.05fr 0.95fr;
    gap: 64px;
  }
`;

const SuccessFigure = styled('figure')`
  margin: 0;

  img {
    display: block;
    width: 100%;
    height: auto;
    object-fit: contain;
  }
`;

// Final call to action

const CtaBox = styled('div')`
  position: relative;
  overflow: hidden;
  padding: 56px 28px;
  border-radius: 24px;
  background: var(--gradient-purple);
  color: var(--primary-foreground);
  text-align: center;
  box-shadow: var(--shadow-glow);

  ${md} {
    padding: 80px 64px;
  }
`;

const CtaGlowTop = styled('div')`
  position: absolute;
  top: -40px;
  right: -40px;
  width: 256px;
  height: 256px;
  border-radius: 9999px;
  background: hsla(27, 100%, 50%, 0.2);
  filter: blur(64px);
  pointer-events: none;
`;

const CtaGlowBottom = styled('div')`
  position: absolute;
  bottom: -64px;
  left: -40px;
  width: 288px;
  height: 288px;
  border-radius: 9999px;
  background: hsla(51, 100%, 51%, 0.2);
  filter: blur(64px);
  pointer-events: none;
`;

const CtaContent = styled('div')`
  position: relative;
  max-width: 48rem;
  margin: 0 auto;
`;

const CtaText = styled('p')`
  max-width: 42rem;
  margin: 24px auto 0;
  font-size: 1.125rem;
  line-height: 1.625;
  color: hsla(0, 0%, 100%, 0.85);
  text-wrap: pretty;

  ${md} {
    font-size: 1.25rem;
  }
`;

const CtaPromise = styled('p')`
  margin: 32px 0 0;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 1.25rem;
  text-wrap: balance;

  ${md} {
    font-size: 1.5rem;
  }
`;

const Tagline = styled('p')`
  margin: 40px 0 0;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.025em;
  color: hsla(0, 0%, 100%, 0.7);
`;
